import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { useDeepCompareEffect } from '@react-hookz/web/useDeepCompareEffect/index.js'
import { addBreadcrumb } from '@sentry/react'
import { useDebouncedCallback } from 'use-debounce'

import { handleNotAwaitedPromise } from '@/sentry/handleNotAwaitedPromise'

import { FormSubmitContext } from './FormSubmitContext'
import { useFormSubmit } from './useFormSubmit'

export interface AutomaticFormSubmitProviderProps {
  readonly wait?: number
}

export const AutomaticFormSubmitProvider: FC<PropsWithChildren<AutomaticFormSubmitProviderProps>> = ({
  children,
  wait = 1000,
}) => {
  const { formState } = useFormContext()
  const formData = useWatch()
  const { submit } = useFormSubmit()

  const debouncedSubmit = useDebouncedCallback(() => {
    handleNotAwaitedPromise(submit())
  }, wait)

  useEffect(
    () => () => {
      debouncedSubmit.flush()
    },
    [debouncedSubmit]
  )

  useDeepCompareEffect(() => {
    if (!formState.isDirty) {
      addBreadcrumb({
        category: 'form',
        level: 'debug',
        message: 'automatic submit cancelled because form is not dirty',
        data: { data: formData },
      })
      debouncedSubmit.cancel()
    } else if (!formState.isValid) {
      addBreadcrumb({
        category: 'form',
        level: 'debug',
        message: 'automatic submit cancelled because form is not valid',
        data: { data: formData, errors: formState.errors },
      })
      debouncedSubmit.cancel()
    } else {
      debouncedSubmit()
    }
  }, [formData, formState.isDirty, formState.isValid])

  const immediateSubmit = useCallback(() => {
    debouncedSubmit.cancel()
    return submit()
  }, [debouncedSubmit, submit])

  const formSubmitContext = useMemo(
    () => ({
      submit: immediateSubmit,
    }),
    [immediateSubmit]
  )

  return <FormSubmitContext.Provider value={formSubmitContext}>{children}</FormSubmitContext.Provider>
}
